// components/AssetRenderer.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Alert, AlertIcon, Text, Code, Spinner } from '@chakra-ui/react';
import { PersonaAsset } from '../types';
import AssetServices from '../services/AssetServices';

interface AssetRendererProps {
	asset: PersonaAsset;
	onError?: (error: string) => void;
	onSuccess?: () => void;
}

const AssetRenderer: React.FC<AssetRendererProps> = ({ asset, onError, onSuccess }) => {
	const [renderError, setRenderError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		renderAsset();
	}, [asset]);

	const renderAsset = async () => {
		setIsLoading(true);
		setRenderError(null);

		try {
			switch (asset.type) {
				case 'html':
					await renderHTML();
					break;
				case 'css':
					await renderCSS();
					break;
				case 'javascript':
					await renderJavaScript();
					break;
				case 'react-component':
					await renderReactComponent();
					break;
				case 'system-prompt-fragment':
					await renderSystemPrompt();
					break;
				case 'media':
					await renderMedia();
					break;
				default:
					throw new Error(`Unsupported asset type: ${asset.type}`);
			}
			onSuccess?.();
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : 'Unknown error';
			setRenderError(errorMsg);
			onError?.(errorMsg);
		} finally {
			setIsLoading(false);
		}
	};

	const renderHTML = async () => {
		if (!containerRef.current) return;

		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		let htmlContent = asset.content;

		// Handle template variables
		if (metadata.template_variables) {
			htmlContent = htmlContent
				.replace(/\{\{personaName\}\}/g, 'Test Persona')
				.replace(/\{\{htmlContent\}\}/g, '<p>Sample content for testing</p>');
		}

		containerRef.current.innerHTML = htmlContent;
	};

	const renderCSS = async () => {
		if (!containerRef.current) return;

		// Create a style element and inject the CSS
		const styleElement = document.createElement('style');
		styleElement.textContent = asset.content;
		document.head.appendChild(styleElement);

		// Store reference to clean up later
		containerRef.current.dataset.styleId = styleElement.id = `asset-style-${asset.id}`;

		// Create a sample container to demonstrate the CSS
		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		const testContainer = document.createElement('div');
		testContainer.className = metadata.applies_to?.replace('.', '') || 'css-test-container';
		testContainer.innerHTML = `
			<h1>CSS Test</h1>
			<h2>Subtitle</h2>
			<p>This is a test paragraph to demonstrate the CSS styling.</p>
			<div class="recipe-highlight">Sample highlighted content</div>
			<div class="ingredient-list">Sample ingredient list</div>
		`;
		containerRef.current.appendChild(testContainer);
	};

	const renderJavaScript = async () => {
		if (!containerRef.current) return;

		// Create a safe execution context
		const scriptContainer = document.createElement('div');
		scriptContainer.innerHTML = '<p>JavaScript asset loaded. Check console for execution.</p>';
		containerRef.current.appendChild(scriptContainer);

		// Execute the JavaScript in a try-catch block
		try {
			const scriptFunction = new Function(asset.content);
			scriptFunction();
		} catch (error) {
			throw new Error(`JavaScript execution error: ${error}`);
		}
	};

	const renderReactComponent = async () => {
		if (!containerRef.current) return;

		try {
			// Create a React component from the asset content
			const ComponentFunction = new Function('useState', 'useEffect', 'React', `
				${asset.content}
				return Component;
			`);

			const Component = ComponentFunction(useState, useEffect, React);

			// For now, show a placeholder since dynamic React rendering is complex
			containerRef.current.innerHTML = `
				<div style="border: 2px dashed #ccc; padding: 20px; text-align: center;">
					<h3>React Component: ${asset.name}</h3>
					<p>Component code parsed successfully. Dynamic rendering requires setup.</p>
					<details>
						<summary>Component Code</summary>
						<pre style="text-align: left; background: #f5f5f5; padding: 10px; margin: 10px 0;">
							<code>${asset.content}</code>
						</pre>
					</details>
				</div>
			`;
		} catch (error) {
			throw new Error(`React component parsing error: ${error}`);
		}
	};

	const renderSystemPrompt = async () => {
		if (!containerRef.current) return;

		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		
		containerRef.current.innerHTML = `
			<div style="border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; background: #f7fafc;">
				<h3 style="margin: 0 0 12px 0; color: #2d3748;">System Prompt Fragment: ${asset.name}</h3>
				<div style="background: white; padding: 12px; border-radius: 4px; margin: 8px 0;">
					<strong>Content:</strong>
					<p style="margin: 8px 0; font-style: italic;">"${asset.content}"</p>
				</div>
				${metadata.conditional ? `
					<div style="background: #ebf8ff; padding: 12px; border-radius: 4px; margin: 8px 0;">
						<strong>Conditional Logic:</strong>
						<code style="display: block; margin: 4px 0; font-size: 12px;">${metadata.conditional}</code>
					</div>
				` : ''}
			</div>
		`;
	};

	const renderMedia = async () => {
		if (!containerRef.current) return;

		// For media assets, we'd typically handle images, videos, audio
		// For now, show a placeholder
		containerRef.current.innerHTML = `
			<div style="border: 2px dashed #ccc; padding: 20px; text-align: center;">
				<h3>Media Asset: ${asset.name}</h3>
				<p>Media content: ${asset.content.length} characters</p>
				<p>This would render actual media content in production.</p>
			</div>
		`;
	};

	// Cleanup function for CSS assets
	useEffect(() => {
		return () => {
			if (asset.type === 'css' && containerRef.current?.dataset.styleId) {
				const styleElement = document.getElementById(containerRef.current.dataset.styleId);
				if (styleElement) {
					styleElement.remove();
				}
			}
		};
	}, [asset.type, asset.id]);

	if (isLoading) {
		return (
			<Box p={4} textAlign="center">
				<Spinner size="lg" />
				<Text mt={2}>Rendering {asset.type} asset...</Text>
			</Box>
		);
	}

	if (renderError) {
		return (
			<Alert status="error">
				<AlertIcon />
				<Box>
					<Text fontWeight="bold">Failed to render {asset.name}</Text>
					<Text fontSize="sm">{renderError}</Text>
				</Box>
			</Alert>
		);
	}

	return (
		<Box>
			<Text fontSize="sm" color="gray.600" mb={2}>
				Asset: {asset.name} ({asset.type})
			</Text>
			<Box
				ref={containerRef}
				border="1px solid"
				borderColor="gray.200"
				borderRadius="md"
				minH="100px"
				p={4}
				bg="white"
			/>
		</Box>
	);
};

export default AssetRenderer;